import { useState } from "react";
import type {
  OwnerProfileResource,
  ReplenishmentRunResource,
  WarehouseResource,
} from "@exonn/js-sdk";
import { ModalV3 } from "src/components/ModalV3/ModalV3.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Select } from "src/components/Select/Select.tsx";
import { Flex } from "src/components/Flex.tsx";
import { useSelect } from "src/system/useSelect.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";

type Props = {
  run: ReplenishmentRunResource;
  supplierCount: number;
  isPending: boolean;
  onSubmit: (body: { owner_profile_id: string; warehouse_id?: string }) => void;
  onClose: () => void;
};

// Отправитель (профиль манданта прогона) и склад доставки, если прогон был по всем складам.
export const ConvertDialog = ({
  run,
  supplierCount,
  isPending,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useResourceFormTranslation();
  const [profileId, setProfileId] = useState("");
  const [warehouseId, setWarehouseId] = useState(run.warehouse_id ?? "");

  const { options: profileOptions } = useSelect<OwnerProfileResource>({
    resource: ResourceEnum.OwnerProfile,
    labelProp: "title",
    filter: {
      items: [{ field: "owner_id", operator: "exact", value: run.owner_id }],
    },
  });
  const { options: warehouseOptions } = useSelect<WarehouseResource>({
    resource: ResourceEnum.Warehouse,
    labelProp: "title",
  });

  return (
    <ModalV3 onClose={onClose}>
      <ModalV3.Positioner>
        <ModalV3.Content>
          <ModalV3.CloseTrigger />
          <ModalV3.Header>
            <ModalV3.Title>{t("convert.title")}</ModalV3.Title>
          </ModalV3.Header>
          <ModalV3.Body style={{ width: 480 }}>
            <p style={{ marginTop: 0 }}>
              {t("convert.description", { count: supplierCount })}
            </p>
            <Flex flexDirection={"column"} gap={"15px"}>
              <Select
                label={t("convert.owner-profile")}
                value={profileId}
                onChange={(value) => setProfileId(value ?? "")}
                options={profileOptions}
              />
              <Select
                label={t("convert.warehouse")}
                value={warehouseId}
                disabled={run.warehouse_id !== null}
                onChange={(value) => setWarehouseId(value ?? "")}
                options={warehouseOptions}
                showSearch
              />
            </Flex>
          </ModalV3.Body>
          <ModalV3.Footer>
            <ModalV3.FooterButtons>
              <Button variant={"outlined"} onClick={onClose}>
                {t("dialog.cancel")}
              </Button>
              <Button
                variant={"filled"}
                disabled={isPending || profileId === "" || warehouseId === ""}
                onClick={() =>
                  onSubmit({
                    owner_profile_id: profileId,
                    ...(run.warehouse_id === null
                      ? { warehouse_id: warehouseId }
                      : {}),
                  })
                }
              >
                {t("convert.submit")}
              </Button>
            </ModalV3.FooterButtons>
          </ModalV3.Footer>
        </ModalV3.Content>
      </ModalV3.Positioner>
    </ModalV3>
  );
};
